
/**
 * Parse JSON response from AI services
 * Handles both direct JSON and JSON embedded in markdown code blocks
 * With enhanced error recovery for malformed JSON
 */
export function parseJsonResponse(content: string): any {
  try {
    // First try direct parsing
    return JSON.parse(content);
  } catch (parseError) {
    // If direct parsing fails, try to extract JSON from potential markdown
    console.log("Failed to parse JSON directly, trying to extract from potential markdown");
    
    // Common patterns for JSON in markdown
    const jsonRegex = /```(?:json)?\s*({[\s\S]*?})\s*```|({[\s\S]*})/m;
    const match = content.match(jsonRegex);
    
    if (match && (match[1] || match[2])) {
      let jsonString = (match[1] || match[2]).trim();
      
      try {
        // Try to parse the extracted JSON
        return JSON.parse(jsonString);
      } catch (secondError) {
        console.error("Failed to parse extracted JSON, attempting to fix common issues:", secondError);
        
        try {
          // Try to fix common JSON formatting issues:
          
          // 1. Fix trailing commas in arrays and objects
          jsonString = jsonString.replace(/,\s*([}\]])/g, "$1");
          
          // 2. Fix missing quotes around property names
          jsonString = jsonString.replace(/(\{|\,)\s*([a-zA-Z0-9_]+)\s*\:/g, '$1"$2":');
          
          // 3. Fix single quotes used instead of double quotes
          jsonString = jsonString.replace(/'/g, '"');
          
          // 4. Fix extra commas in arrays
          jsonString = jsonString.replace(/\[\s*,/g, '[');
          jsonString = jsonString.replace(/,\s*,/g, ',');
          jsonString = jsonString.replace(/,\s*\]/g, ']');
          
          // 5. Fix broken arrays with missing brackets
          if (jsonString.includes('[') && !jsonString.includes(']')) {
            jsonString = jsonString.replace(/\[(.*?)$/, '[$1]');
          }
          
          // 6. Fix unclosed strings
          const unclosedQuotesRegex = /"([^"]*)$/g;
          jsonString = jsonString.replace(unclosedQuotesRegex, '"$1"');
          
          // 7. Fix trailing unescaped quotes in strings
          jsonString = jsonString.replace(/"([^"]*)"([^"]*)"([^"]*)"/g, function(match, p1, p2, p3) {
            return `"${p1}${p2.replace(/"/g, '\\"')}${p3}"`;
          });
          
          // Try parsing the fixed JSON
          return JSON.parse(jsonString);
        } catch (thirdError) {
          // If all attempts fail, log error and extract partial data
          console.error("All attempts to parse JSON failed:", thirdError);
          console.error("Malformed JSON content:", content);
          console.error("After repair attempts:", jsonString);
          
          // Instead of failing completely, return a partial object with what we can extract
          return extractPartialData(content);
        }
      }
    } else {
      console.error("Could not extract JSON from response:", content);
      // Return partial data instead of failing completely
      return extractPartialData(content);
    }
  }
}

/**
 * Extract partial data from malformed JSON or text content
 * This is a last resort to get at least some usable data rather than failing completely
 */
function extractPartialData(content: string): any {
  const result: Record<string, any> = {};
  
  // Try to extract investment focus
  const focusMatch = content.match(/["""']?investmentFocus["""']?\s*[:=]\s*["""']([^"""']+)["""']/i);
  if (focusMatch && focusMatch[1]) {
    result.investmentFocus = focusMatch[1].trim();
  }
  
  // Try to extract portfolio companies
  const portfolioRegex = /portfolioCompanies["""']?\s*[:=]\s*\[(.*?)\]/is;
  const portfolioMatches = content.match(portfolioRegex);
  if (portfolioMatches && portfolioMatches[1]) {
    try {
      // Try to extract items between quotes in the array
      const companiesText = portfolioMatches[1];
      const companies = [];
      const companyRegex = /["""']([^"""']+)["""']/g;
      let match;
      while ((match = companyRegex.exec(companiesText)) !== null) {
        companies.push(match[1].trim());
      }
      result.portfolioCompanies = companies.filter(Boolean);
    } catch (e) {
      // Fallback to empty array
      result.portfolioCompanies = [];
    }
  }
  
  // Try to extract keywords
  const keywordsRegex = /keywords["""']?\s*[:=]\s*\[(.*?)\]/is;
  const keywordsMatches = content.match(keywordsRegex);
  if (keywordsMatches && keywordsMatches[1]) {
    try {
      // Try to extract items between quotes in the array
      const keywordsText = keywordsMatches[1];
      const keywords = [];
      const keywordRegex = /["""']([^"""']+)["""']/g;
      let match;
      while ((match = keywordRegex.exec(keywordsText)) !== null) {
        keywords.push(match[1].trim());
      }
      result.keywords = keywords.filter(Boolean);
    } catch (e) {
      // Fallback to empty array
      result.keywords = [];
    }
  }
  
  // Try to extract website
  const websiteMatch = content.match(/website["""']?\s*[:=]\s*["""']([^"""']+)["""']/i);
  if (websiteMatch && websiteMatch[1]) {
    result.website = websiteMatch[1].trim();
  }
  
  // Try to extract relevance score
  const scoreMatch = content.match(/relevanceScore["""']?\s*[:=]\s*([0-9\.]+)/i);
  if (scoreMatch && scoreMatch[1]) {
    const score = parseFloat(scoreMatch[1].trim());
    if (!isNaN(score)) {
      result.relevanceScore = score;
    }
  }
  
  // Try to extract confidence level
  const confidenceMatch = content.match(/confidence["""']?\s*[:=]\s*["""']([^"""']+)["""']/i);
  if (confidenceMatch && confidenceMatch[1]) {
    result.confidence = confidenceMatch[1].trim();
  }
  
  // Try to extract notes
  const notesMatch = content.match(/notes["""']?\s*[:=]\s*["""']([^"""']+)["""']/i);
  if (notesMatch && notesMatch[1]) {
    result.notes = notesMatch[1].trim();
  }
  
  // Add confidence indication that this was extracted through fallback
  result.confidence = "low";
  result.notes = (result.notes || "") + " (Extracted from malformed response)";
  
  console.log("Extracted partial data through fallback mechanism:", result);
  return result;
}
